import { Navigate, Outlet, NavLink } from "react-router-dom";
import classNames from "classnames";
import { capitalizeFirstLetter } from "../../utils";
import { menu } from "./menu.map";
import { useConfig } from "../../hooks";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";

const Settings = () => {
  const { appType, configs } = useConfig();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const featureFlags = configs?.featureFlags || {};
  const isFeatureEnabled = (featureKey) => featureFlags?.[featureKey] !== false;

  // Map Settings menu labels to feature keys.
  // Note: "General" stays always-on within Settings for now (avoid empty landing page).
  const settingsFeatureKeyByLabel = {
    Rates: "settings.rates.enabled",
    Dispatch: "settings.dispatch.enabled",
    Templates: "settings.templates.enabled",
    "Task Templates": "settings.taskTemplates.enabled",
    Tags: "settings.tags.enabled",
    "Notification Templates": "settings.notificationTemplates.enabled",
    Users: "settings.users.enabled",
    "Roles Permissions": "settings.users.enabled",
    Billing: "settings.billing.enabled",
    Reports: "settings.reports.enabled",
    "Google Drive": "settings.enabled",
    Advanced: "settings.advanced.enabled",
  };
  const settingsPermissionByLabel = {
    Users: PERMISSIONS.SETTINGS_USERS_READ,
    "Roles Permissions": PERMISSIONS.SETTINGS_ROLES_READ,
    Billing: PERMISSIONS.SETTINGS_BILLING_READ,
    Tags: PERMISSIONS.SETTINGS_TAGS_MANAGE,
    Templates: PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
    "Task Templates": PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
    "Notification Templates": PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
    "Google Drive": PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE,
    Advanced: PERMISSIONS.SETTINGS_ADVANCED_MANAGE,
  };
  const hasSettingsAccess = hasAnyPermission([
    PERMISSIONS.SETTINGS_USERS_READ,
    PERMISSIONS.SETTINGS_USERS_MANAGE,
    PERMISSIONS.SETTINGS_ROLES_READ,
    PERMISSIONS.SETTINGS_ROLES_MANAGE,
    PERMISSIONS.SETTINGS_BILLING_READ,
    PERMISSIONS.SETTINGS_BILLING_MANAGE,
    PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE,
  ]);

  if (!hasSettingsAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="h-full">
      <div className="grid grid-cols-6 divide-x border-t h-full">
        <div className="p-6 bg-white">
          {Object.keys(menu).map((title, key) => {
            return (
              <div key={key} className="space-y-2">
                <div>
                  <span className="font-medium">
                    {capitalizeFirstLetter(title)}
                  </span>
                </div>
                <div className="space-y-2 flex flex-col">
                  {menu[title].map((item, idx) => {
                    const featureKey = settingsFeatureKeyByLabel?.[item];
                    const permissionKey = settingsPermissionByLabel?.[item];
                    if (featureKey && !isFeatureEnabled(featureKey)) {
                      return null;
                    }
                    if (permissionKey && !hasPermission(permissionKey)) {
                      return null;
                    }
                    if (
                      !permissionKey &&
                      item !== "General" &&
                      !hasSettingsAccess
                    ) {
                      return null;
                    }
                    if (item === "Reports" && appType === "trucking") {
                      return null;
                    }
                    return (
                      <NavLink
                        key={idx}
                        to={`/settings/${item
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          classNames(
                            "text-gray-500 cursor-pointer hover:text-gray-600",
                            { "text-primary-700 font-semibold": isActive },
                          )
                        }
                      >
                        {item}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="col-span-5 space-y-4 overflow-scroll p-3">
          <div className="space-y-2 overflow-y-scroll">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

export const SettingsHeader = (props) => {
  return (
    <div className="flex items-center justify-between">
      <div className="text-gray-800 text-2xl font-bold">{props.title}</div>
      <div>{props.actions}</div>
    </div>
  );
};
